
import { supabase } from '../../supabase';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { getPublicUrl } from './url-service';

/**
 * Uploads a file to Supabase Storage with security headers
 * @param file File to upload
 * @param userId User ID for path organization
 * @param securityToken Security token for upload validation
 * @param timestamp Timestamp for security verification
 * @returns Public URL of the uploaded file or null if upload failed
 */
export async function uploadFileToStorage(
  file: File, 
  userId: string, 
  securityToken: string, 
  timestamp: number
): Promise<string | null> {
  try {
    // Generate a unique filename with UUID to prevent path traversal attacks
    const sanitizedFileName = `${uuidv4()}.pdf`;
    // Add user ID prefix for better organization and additional security
    const filePath = `${userId}/${sanitizedFileName}`;
    
    console.log(`Uploading file to ${filePath} in papers bucket`);
    
    // Upload the file to Supabase Storage with content type validation and security metadata
    const { data, error } = await supabase.storage
      .from('papers')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'application/pdf', // Explicitly set content type
        duplex: 'half',
        headers: {
          'X-Upload-Token': securityToken, 
          'X-Upload-Timestamp': timestamp.toString()
        }
      });
    
    if (error) {
      console.error('Error uploading file:', error);
      
      if (error.message.includes('storage/duplicate_error')) {
        toast.error('File already exists', {
          description: 'A file with this name already exists in storage'
        });
      } else if (error.message.includes('Unauthorized')) {
        toast.error('Unauthorized', {
          description: 'You do not have permission to upload files'
        });
      } else {
        toast.error('File upload failed', {
          description: error.message
        });
      }
      return null;
    }
    
    console.log('File uploaded successfully:', data);
    
    // Get the public URL for the uploaded file
    return getPublicUrl(filePath);
  } catch (error) {
    console.error('Error in storage operation:', error);
    return null;
  }
}
